import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Download, Trash2 } from "lucide-react";
import { http } from "@/lib/api";
import { Card } from "@/components/portal/ui";

const RES_LABEL = {
  "night-waking-checklist": "Night Waking Checklist", "short-nap-checklist": "Short Nap Checklist",
  "bedtime-troubleshooting-guide": "Bedtime Troubleshooting", "crib-sleep-observation-guide": "Crib Sleep Observation",
  "4-month-sleep-changes-guide": "4-Month Sleep Changes",
};

export default function AdminLeads() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => http.get("/admin/leads").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-[#6B665E]">Loading…</div>;

  const delLead = async (l) => {
    if (!window.confirm(`Delete this test lead (${l.email})? This cannot be undone.`)) return;
    try { await http.delete(`/admin/leads/${l.id}`); toast.success("Lead deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const exportCsv = () => {
    const rows = [["First name", "Email", "Resource", "Date"]].concat(
      data.leads.map((l) => [l.first_name, l.email, RES_LABEL[l.resource] || l.resource,
        l.created_at ? new Date(l.created_at).toISOString() : ""]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "resource-leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="eyebrow text-[#8A998C] mb-1">Academics</div>
      <div className="flex items-center justify-between gap-4 mb-1">
        <h1 className="font-display text-3xl text-[#1A1918]">Admissions &amp; Interest Lists</h1>
        {data.leads.length > 0 && (
          <button onClick={exportCsv} data-testid="leads-export" className="flex items-center gap-1.5 text-sm border border-[#E5E1D8] rounded-full px-4 py-2 text-[#6B665E] hover:border-[#9F634E]/50 transition-colors">
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>
      <p className="text-[#6B665E] text-sm mb-6">Course interest registrations (not enrollments) and free checklist downloads. Click a count to filter.</p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="leads-counts">
        <button onClick={() => setFilter("")} className={`text-sm rounded-full px-3 py-1.5 ${!filter ? "bg-[#1A1918] text-white" : "bg-[#F3EFE9] text-[#6B665E]"}`}>{data.total} total</button>
        {Object.entries(data.by_resource).map(([k, v]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`leads-filter-${k.replace(/[^A-Za-z0-9]+/g, "-")}`} className={`text-sm rounded-full px-3 py-1.5 ${filter === k ? "bg-[#1A1918] text-white" : "bg-[#F3EFE9] text-[#6B665E]"}`}>{RES_LABEL[k] || k}: {v}</button>
        ))}
      </div>

      {data.leads.length === 0 ? (
        <Card className="p-8 text-center text-[#6B665E]">No interest registrations or checklist downloads yet.</Card>
      ) : (
        <Card className="p-0 overflow-hidden" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#8A998C] border-b border-[#F0ECE4]">
              <th className="p-4 font-medium">Name</th><th className="p-4 font-medium">Email</th>
              <th className="p-4 font-medium">Resource</th><th className="p-4 font-medium">Date</th><th className="p-4 font-medium"></th>
            </tr></thead>
            <tbody>
              {data.leads.filter((l) => !filter || l.resource === filter).map((l, i) => (
                <tr key={l.id || i} className="border-b border-[#F7F4EE] last:border-0" data-testid={`lead-row-${i}`}>
                  <td className="p-4 text-[#1A1918]">{l.first_name || "—"}{l.is_test && <span className="ml-2 text-[9px] uppercase bg-[#C49A5A]/20 text-[#8a6a2e] rounded-full px-1.5 py-0.5">Test</span>}</td>
                  <td className="p-4"><a href={`mailto:${l.email}`} className="text-[#9F634E] hover:underline flex items-center gap-1.5"><Mail size={13} /> {l.email}</a></td>
                  <td className="p-4 text-[#6B665E]">{RES_LABEL[l.resource] || l.resource}</td>
                  <td className="p-4 text-[#8A998C]">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-4 text-right">{l.deletable && (
                    <button onClick={() => delLead(l)} data-testid={`lead-delete-${i}`} title="Delete test lead"
                      className="inline-flex items-center gap-1 text-xs text-[#9E4F4F] hover:underline"><Trash2 size={13} /> Delete</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
